import { useEffect, useState } from 'react'

import { API } from '@/api'
import { GetNATListRes } from '@/api/type-response'
import { ServerNATInfo } from '@/api/type-server'
import { Button, EmptyView, Loading, Modal, Radio, Trans } from '@/components'
import { useDomain } from '@/contexts/DomainContext'

export function BindNATModal(props: {
  open: boolean
  domain?: number
  boundNAT?: { resID: string } | null
  onClose: () => void
  onOk: (selectedIp?: ServerNATInfo) => void
}) {
  const { open, domain, boundNAT, onClose, onOk } = props
  const [loading, setLoading] = useState(false)
  const [nats, setNats] = useState<ServerNATInfo[]>([])
  const [selectedRowKey, setSelectedRowKey] = useState<string | null>(null)
  const { domainInfos } = useDomain()

  useEffect(() => {
    if (!open || !domain) return
    setLoading(true)
    // Reset selectedRowKey when modal opens
    setSelectedRowKey(boundNAT?.resID || null)
    API.getNATList({
      network_type: 6,
      regions: [domain],
      region_filter_criteria: 0,
      page: 1,
      per_page: 10,
    })
      .then((res: GetNATListRes) => {
        setNats(res.networks ?? [])
      })
      .finally(() => setLoading(false))
  }, [open, domain, boundNAT])

  return (
    <Modal className="w-[640px]" open={open} onClose={onClose}>
      <Modal.Header className="text-secondary t-sb">
        <Trans i18nKey="create.bindNATModal.title">Bind Public IP Address</Trans>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : nats.length === 0 ? (
          <EmptyView
            title={<Trans i18nKey="create.bindNATModal.noDataTitle">No Public IP available</Trans>}
            imageSize="small"
            className="text-center"
            content={
              <Trans i18nKey="create.bindNATModal.noDataContent">
                No public IP address is available at the moment. <br />
                Please try again later.
              </Trans>
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="table mt-4 w-full">
              <thead>
                <tr>
                  <th></th>
                  <th>
                    <Trans i18nKey="create.bindPublicIpTableRegion">Region</Trans>
                  </th>
                  <th>
                    <Trans i18nKey="create.bindPublicIpTableResId">ID</Trans>
                  </th>
                  <th className="!text-right">
                    <Trans i18nKey="create.bindPublicIpTableIp">Public IP Address</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {nats.map((nat) => (
                  <tr
                    key={nat.resID}
                    className={
                      'cursor-pointer' + (selectedRowKey === nat.resID ? ' bg-panel-gray-100' : '')
                    }
                    onClick={() => setSelectedRowKey(nat.resID)}
                  >
                    <td className="w-5">
                      <Radio
                        checked={selectedRowKey === nat.resID}
                        onChange={() => setSelectedRowKey(nat.resID)}
                      />
                    </td>
                    <td>
                      <div className="text-primary t-sb">
                        {domainInfos.find((info) => info.domain === nat.domain)?.displayName ||
                          nat.domain}
                      </div>
                    </td>
                    <td>
                      <div className="text-primary t-sb">{nat.resID}</div>
                    </td>
                    <td className="text-right">
                      <div className="text-primary t-sb">{nat.name}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={() => {
            const selectedIp = nats.find((nat) => nat.resID === selectedRowKey)
            onOk(selectedIp)
          }}
          disabled={!selectedRowKey}
        >
          <Trans i18nKey="create.bindNATModal.confirmBtn">Bind</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
